import {
  AUDIO_BATCH_MAX_FILE_BYTES,
  AUDIO_BATCH_MAX_FILES,
  AUDIO_BATCH_MAX_TOTAL_FILE_BYTES,
  AUDIO_BATCH_VIDEO_EXTENSIONS,
  audioExtensionFromMimeAndName,
  type AudioBatchExtension,
  type AudioBatchValidationError,
} from './types'

/**
 * Resolve the canonical extension for a browser-side `File` (audio or video).
 * Delegates to the shared resolver so the optimistic client-side filter and the
 * authoritative server-side validator cannot disagree about what counts as a
 * supported media file.
 */
export function resolveAudioExtensionFromFile(file: File): AudioBatchExtension | null {
  return audioExtensionFromMimeAndName(file.type || null, file.name)
}

export function isVideoBatchFile(file: File): boolean {
  const extension = resolveAudioExtensionFromFile(file)
  return (
    extension !== null && (AUDIO_BATCH_VIDEO_EXTENSIONS as readonly string[]).includes(extension)
  )
}

export interface ValidatedAudioBatchAddition {
  /** Files that should be added to the pending queue. */
  accepted: File[]
  /** Errors describing every file that was rejected. */
  errors: AudioBatchValidationError[]
}

/**
 * Validate an attempted append to the pending file queue.
 *
 * Pure function: takes the current queue and the new files the user just
 * dropped/picked, returns the subset that survives the limits plus a list
 * of errors describing every rejection. The UI renders the errors as
 * dismissible chips so the user can fix and retry without re-doing the
 * good ones.
 *
 * Why filter on the client at all when the server validates again? Two
 * reasons:
 * 1. Big-file UX — uploading 500 MB only to be told "wrong format" wastes
 *    operator time + bandwidth.
 * 2. The drop zone needs to know the queue length to render its
 *    "X / 5 files queued" hint, which depends on the validated set.
 *
 * The server is still the boundary that gets to say no — this is purely
 * a usability layer.
 */
export function validateAdditions(
  currentQueueSize: number,
  candidates: File[],
  currentQueueBytes = 0,
  maxFileBytes = AUDIO_BATCH_MAX_FILE_BYTES
): ValidatedAudioBatchAddition {
  const accepted: File[] = []
  const errors: AudioBatchValidationError[] = []
  let acceptedBytes = currentQueueBytes

  if (candidates.length === 0) {
    return { accepted, errors }
  }

  const remainingSlots = Math.max(0, AUDIO_BATCH_MAX_FILES - currentQueueSize)

  if (candidates.length > remainingSlots) {
    errors.push({
      kind: 'too_many_files',
      received: currentQueueSize + candidates.length,
      max: AUDIO_BATCH_MAX_FILES,
    })
  }

  for (const file of candidates.slice(0, remainingSlots)) {
    const extension = resolveAudioExtensionFromFile(file)
    if (!extension) {
      errors.push({
        kind: 'unsupported_format',
        filename: file.name,
        mimeType: file.type || null,
      })
      continue
    }

    // A 0-byte pick has no audio to transcribe, and the two upload paths
    // disagree about it server-side: the staged/chunked initiate rejects the
    // WHOLE batch, while the single-request path accepts it and fails the item
    // later. Stopping it here keeps one good file from being lost to a bad one.
    if (file.size === 0) {
      errors.push({ kind: 'empty_file', filename: file.name })
      continue
    }

    if (file.size > maxFileBytes) {
      errors.push({
        kind: 'file_too_large',
        filename: file.name,
        size: file.size,
        max: maxFileBytes,
      })
      continue
    }

    if (acceptedBytes + file.size > AUDIO_BATCH_MAX_TOTAL_FILE_BYTES) {
      errors.push({
        kind: 'total_size_too_large',
        size: acceptedBytes + file.size,
        max: AUDIO_BATCH_MAX_TOTAL_FILE_BYTES,
      })
      continue
    }

    accepted.push(file)
    acceptedBytes += file.size
  }

  return { accepted, errors }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Render a structured {@link AudioBatchValidationError} as a localized,
 * user-facing string. Shared by every upload surface (the consolidated upload
 * modal and the legacy batch workspace) so the error copy stays in one place —
 * all keys live under `batchTranscription.validationError.*`.
 */
export function describeValidationError(
  err: AudioBatchValidationError,
  t: (key: string, values?: Record<string, string | number>) => string
): string {
  switch (err.kind) {
    case 'no_files':
      return t('batchTranscription.validationError.noFiles')
    case 'too_many_files':
      return t('batchTranscription.validationError.tooManyFiles', {
        max: err.max,
        received: err.received,
      })
    case 'file_too_large':
      return t('batchTranscription.validationError.fileTooLarge', {
        filename: err.filename,
        size: formatFileSize(err.size),
        max: formatFileSize(err.max),
      })
    case 'total_size_too_large':
      return t('batchTranscription.validationError.totalSizeTooLarge', {
        size: formatFileSize(err.size),
        max: formatFileSize(err.max),
      })
    case 'empty_file':
      return t('batchTranscription.validationError.emptyFile', {
        filename: err.filename,
      })
    case 'unsupported_format':
      return err.mimeType
        ? t('batchTranscription.validationError.unsupportedFormatWithMime', {
            filename: err.filename,
            mimeType: err.mimeType,
          })
        : t('batchTranscription.validationError.unsupportedFormat', {
            filename: err.filename,
          })
    case 'invalid_file':
      return t('batchTranscription.validationError.invalidFile', {
        filename: err.filename,
        reason: err.reason,
      })
    case 'invalid_enhancement_hints':
      return t('batchTranscription.validationError.invalidEnhancementHints')
    case 'invalid_visual_context':
      return t('batchTranscription.validationError.invalidVisualContext')
    case 'visual_context_requires_video':
      return t('batchTranscription.validationError.visualContextRequiresVideo')
    case 'invalid_notes_template':
      return t('transcriptions.upload.invalidNotesTemplate')
    case 'notes_template_not_found':
      return t('transcriptions.upload.notesTemplateNotFound')
    case 'invalid_operation_id':
      // Server-emitted only (staging initiate); reaching the browser means the
      // client-generated operation id was malformed.
      return t('batchTranscription.validationError.invalidOperationId')
    case 'invalid_source_url':
      return t('batchTranscription.validationError.invalidSourceUrl', {
        position: err.index + 1,
      })
    case 'too_many_source_urls':
      return t('batchTranscription.validationError.tooManySourceUrls', {
        max: err.max,
        received: err.received,
      })
    case 'mixed_sources':
      return t('batchTranscription.validationError.mixedSources')
    case 'invalid_markers':
      // Server-emitted only — the web upload surface never sends `markers`
      // itself (mobile-only feature); reaching the browser means an
      // internal/M2M caller sent a malformed marker array.
      return t('batchTranscription.validationError.invalidMarkers')
    case 'markers_require_single_file':
      return t('batchTranscription.validationError.markersRequireSingleFile')
    case 'invalid_share_to_team':
      return t('batchTranscription.validationError.shareToTeamInvalid')
    case 'team_context_required':
      return t('batchTranscription.validationError.teamContextRequired')
    default:
      // Deliberately NOT a `never`-typed exhaustiveness check: the API and
      // web app ship on separate release cadences (see AGENTS.md), so a
      // web bundle already in the browser can receive a `kind` the API
      // started emitting after that bundle was built — a case TypeScript's
      // compile-time check cannot see. AGENTS.md bans silent failure, and
      // this is exactly the bug this file is fixing (an unhandled `kind`
      // silently rendered as "undefined"), so a generic user-facing
      // fallback is required here regardless of what the type system
      // proves at build time. New known kinds should still get their own
      // branch above for a specific, actionable message.
      return t('batchTranscription.validationError.unknown')
  }
}
